import { html } from 'lit-element';
import { CustomLitElement } from '../CustomLitElement.js';

import {
  bubbleStyles,
  choiceValueStyles,
  responseStyles,
} from '../cssUtils.js';
import { getIcon, renderBubble } from '../utils.js';
import {
  defineElement,
  importMathRendering,
} from '../classroom-tool-extensions/classifier/utils.js';
import { PieMathRenderer } from '../classroom-tool-extensions/common/PieMathRenderer.js';

/**
 * @element inline-dropdown-response
 * @prop {Object} model - The model for the pie
 * @prop {Object} session - The response session
 * @cssprop --pie-live-session-background-color - change the background color
 * @cssprop --pie-live-session-text-color - change the text color
 * @cssprop --pie-live-session-bubble-text-color - change the text color
 * @cssprop --pie-live-session-font-family - change the font family
 * @cssprop --pie-live-session-font-size-small - change the font size
 * @cssprop --pie-live-session-response-height - change the height (must be valid, eg: 100px, 10em )
 * @cssprop --pie-live-session-response-width - change the width (must be valid, eg: 100px, 10em )
 */
export class InlineDropdownResponse extends CustomLitElement {
  static get properties() {
    return {
      model: { type: Object, attribute: false },
      session: { type: Array, attribute: false },
      hoveredChoice: { type: Object },
    };
  }

  set hoveredChoice(val) {
    const oldVal = this._hoveredChoice;

    if (!oldVal || !val || oldVal.value !== val.value) {
      this._hoveredChoice = val;
      this.requestUpdate('hoveredChoice', oldVal);
    }
  }

  get hoveredChoice() {
    return this._hoveredChoice;
  }

  constructor() {
    super();
    this._hoveredChoice = null;
  }

  createRenderRoot() {
    return this;
  }

  updated(_changedProperties) {
    super.updated(_changedProperties);

    defineElement('math-rendering-element', PieMathRenderer);

    if (!this.mathRenderingModuleUrlImported) {
      importMathRendering();

      this.mathRenderingModuleUrlImported = true;
    }
  }

  mouseover(e, value) {
    const item = e.target.closest('.choice-value');

    if (item && item.getBoundingClientRect) {
      const bounds = item.getBoundingClientRect();

      this.hoveredChoice = {
        value,
        top: bounds.top + bounds.height,
        left: bounds.left + bounds.width,
      };
    }
  }

  mouseout() {
    this.hoveredChoice = null;
  }

  _renderResponse() {
    const { choices } = this.model || {};
    const { value } = this.session || {};

    if (!value || !choices) return html``;

    return Object.keys(choices).map(v => {
      const { label, correct } =
        choices[v].find(c => c.value === value[v]) || {};
      const tag = !value[v] ? '' : label || '';

      return html`<div
        class="choice-value choice-content"
        @mouseover=${e => this.mouseover(e, tag)}
        @mouseout=${this.mouseout}
      >
        <p class="choice-index">
          ${parseInt(v, 10) + 1}.
          <span class="choice-icon">${tag ? getIcon(correct) : ''}</span>
        </p>
        <math-rendering-element .label="${label}"></math-rendering-element>
        <div class="choice-overflow"></div>
      </div>`;
    });
  }

  render() {
    return html`
      <style>
        ${bubbleStyles}
          ${choiceValueStyles}
          ${responseStyles}
          .inline-dropdown
          p {
          margin-left: 2px;
          margin-top: 2px;
          margin-bottom: 2px;
        }
      </style>
      <div class="response inline-dropdown">
        ${this._renderResponse()} ${renderBubble(this.hoveredChoice)}
      </div>
    `;
  }
}

export default InlineDropdownResponse;
